import { useState } from 'react'
import { adminDeleteTransaction } from '@/actions/adminActions'
import { Button, Alert, Dialog } from '@/ui'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { parseErrorFromCatch } from '@/utils/errorHelpers'

export const useDeleteTransaction = ({
  transaction,
  onDeleted = () => undefined,
}: {
  transaction: Transaction
  onDeleted?: () => void
}) => {
  const dispatch = useAppDispatch()
  const [isDeleting, setDeleting] = useState(false)
  const [open, setOpen] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const deleteTransaction = async () => {
    setDeleting(true)
    setError(null)
    try {
      await adminDeleteTransaction(Number(transaction.id))(dispatch)
      onDeleted()
      setDeleting(false)
      setOpen(false)
    } catch (err) {
      // Held open on failure: a reconciled transaction is refused here, and closing
      // would drop the server's explanation of which period to reopen.
      setError(parseErrorFromCatch(err))
      setDeleting(false)
    }
  }

  const renderConfirm = () => (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setOpen(false)
          setError(null)
        }
      }}
    >
      <Dialog.Content>
        <Dialog.Title>Delete transaction</Dialog.Title>
        {error && <Alert variant="error">{error}</Alert>}
        <Alert variant="warning">
          Please make sure that you are 100% certain you want to delete this
          transaction.
        </Alert>
        <p>
          <b>Transaction:</b> {transaction.description}
        </p>
        <p>
          <b>ID:</b> {transaction.id}
        </p>
        <Dialog.Footer>
          <Button onClick={() => setOpen(false)} variant="primary">
            Cancel
          </Button>
          <Button onClick={() => deleteTransaction()}>Delete</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )

  return {
    renderConfirm,
    isDeleting,
    showConfirm: () => {
      setError(null)
      setOpen(true)
    },
  }
}
